import { AccountFilter, IntFilter } from '@trm/generated/types';

export const matchEitherSubscriptionId = (condition: IntFilter): AccountFilter[] => [
  { subscriptionId: condition },
  { nmiSubscriptionId: condition },
];

/**
 * A complete machine-generated account name. Every generation site in the api
 * builds `{PREFIX}-{platformUserId}-{8 random digits}` — see
 * api/src/processes/helpers/combineNaming.ts plus generate8RandomNumbers()
 * (topstepxCreateAccount, resets.service), e.g.
 * EXPRESS-V2-DLL-648864-10890824 / 50KTC-V2-... / PRAC-V2-... — and archival
 * renames append `-ARCHIVED-{3 chars}` (transferToLiveAccount.process.ts).
 * Prefixes vary by product and admins can rename accounts, so this matches the
 * generated tail shape rather than a prefix list.
 */
const GENERATED_ACCOUNT_NAME =
  /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*-\d+-\d{8}(?:-ARCHIVED-[A-Za-z0-9]{3})?$/i;

export const isGeneratedAccountName = (value: string): boolean =>
  GENERATED_ACCOUNT_NAME.test(value);

/**
 * Index-friendly equality filter for a pasted account name. Generated names
 * are stored uppercase, so also try the uppercased input for users who type
 * one by hand; `in` on the indexed accountName column stays a fast lookup,
 * unlike the includesInsensitive scan the simple search otherwise falls back to.
 */
export const matchAccountNameExact = (value: string): AccountFilter[] => [
  { accountName: { in: [...new Set([value, value.toUpperCase()])] } },
];

/**
 * Routes the accounts simple search to the cheapest filter the input allows:
 * a full generated account name becomes an exact match on the indexed
 * accountName column, anything containing '@' can only be an email so the
 * accountName/userFullName scans are skipped, and only true free text pays
 * for the three-way includesInsensitive OR across the user joins.
 */
export const matchSimpleSearch = (value: string): AccountFilter[] => {
  if (isGeneratedAccountName(value)) {
    return matchAccountNameExact(value);
  }
  if (value.includes('@')) {
    return [{ user: { email: { includesInsensitive: value } } }];
  }
  return [
    { accountName: { includesInsensitive: value } },
    { user: { email: { includesInsensitive: value } } },
    { user: { userContactInfo: { userFullName: { includesInsensitive: value } } } },
  ];
};
